import { useState, createContext, useContext, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./auth";
import toast from "react-hot-toast";

const WishlistContext = createContext();

const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);
  const [auth] = useAuth();

  // Load wishlist from DB whenever auth token changes
  useEffect(() => {
    if (auth?.token) {
      fetchWishlist();
    } else {
      setWishlist([]); // logout → clear
    }
  }, [auth?.token]);

  // ─── FETCH ─────────────────────────────────────────────────────────────────
  const fetchWishlist = async () => {
    try {
      const { data } = await axios.get("/wishlist");
      if (data?.success) {
        setWishlist(data.products);
      }
    } catch (err) {
      console.log("Wishlist fetch error:", err);
    }
  };

  // ─── ADD ───────────────────────────────────────────────────────────────────
  const addToWishlist = async (productId) => {
    try {
      const { data } = await axios.post("/wishlist/add", { productId });

      if (data?.message === "Already in wishlist") {
        toast("Already in your wishlist ❤️");
        return;
      }

      if (data?.success) {
        toast.success("Added to wishlist ❤️");
        await fetchWishlist();
      }
    } catch (err) {
      console.log("Add wishlist error:", err);
      toast.error("Failed to add to wishlist");
    }
  };

  // ─── REMOVE ────────────────────────────────────────────────────────────────
  const removeFromWishlist = async (productId, silent = false) => {
    try {
      const { data } = await axios.delete(`/wishlist/${productId}`);

      if (data?.success) {
        if (!silent) toast.success("Removed from wishlist");
        setWishlist((prev) => prev.filter((p) => p._id !== productId));
      }
    } catch (err) {
      console.log("Remove wishlist error:", err);
      toast.error("Failed to remove from wishlist");
    }
  };

  // ─── CLEAR ─────────────────────────────────────────────────────────────────
  const clearWishlist = async () => {
    try {
      const { data } = await axios.delete("/wishlist");
      if (data?.success) {
        toast.success("Wishlist cleared");
        setWishlist([]);
      }
    } catch (err) {
      console.log("Clear wishlist error:", err);
      toast.error("Failed to clear wishlist");
    }
  };

  // ─── CHECK ─────────────────────────────────────────────────────────────────
  const isInWishlist = (productId) => {
    return wishlist.some((p) => p._id === productId);
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        setWishlist,
        fetchWishlist,
        addToWishlist,
        removeFromWishlist,
        clearWishlist,
        isInWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

const useWishlist = () => useContext(WishlistContext);

export { useWishlist, WishlistProvider };
